import { ProductManager } from '../productManager';
import type { Product } from '@smartphone-tycoon/shared-core';

export interface MarketShareData {
  productId: string;
  companyId: string;
  productName: string;
  unitsSold: number;
  marketShare: number;
  previousMarketShare?: number;
}

export interface MarketAnalysis {
  totalMarketSize: number;
  totalUnitsSold: number;
  marketShares: MarketShareData[];
  playerMarketShare: number;
  competitorMarketShares: Map<string, number>;
  lastUpdated: Date;
}

export class MarketShareService {
  private static instance: MarketShareService;
  private lastUpdateTime: number = 0;
  private updateInterval: number = 1; // Update every game day for accurate market share

  private constructor() {}

  public static getInstance(): MarketShareService {
    if (!MarketShareService.instance) {
      MarketShareService.instance = new MarketShareService();
    }
    return MarketShareService.instance;
  }

  /**
   * Invalidate the cache to force recalculation on next call.
   * Call this when new products are added to the market.
   */
  public invalidateCache(): void {
    this.lastUpdateTime = 0;
  }

  /**
   * Calculate market share for all products based on units sold
   * Should be called periodically (e.g., monthly in game time)
   */
  public async calculateMarketShares(currentGameDay: number): Promise<MarketAnalysis> {
    try {
      // Check if we need to update (monthly intervals)
      if (currentGameDay - this.lastUpdateTime < this.updateInterval) {
        // Return cached data if available
        return this.getCachedMarketAnalysis();
      }

      console.log(`📊 Market Share Service: Calculating market shares for day ${currentGameDay}`);
      
      // Get all products from the database (both player and competitor products)
      const allProductsResult = await ProductManager.getAllProducts();
      
      if (!allProductsResult.success || !allProductsResult.data) {
        throw new Error('Failed to fetch products for market share calculation');
      }
      
      const allProducts = allProductsResult.data;
      
      // Filter only active products (not discontinued)
      const activeProducts = allProducts.filter((product: Product) => 
        product.status !== 'discontinued'
      );

      // Calculate total units sold across all products
      let totalUnitsSold = 0;
      const productSalesData: Array<{
        product: Product;
        unitsSold: number;
      }> = [];

      for (const product of activeProducts) {
        const unitsSold = product.salesMetrics?.unitsSold || 0;
        totalUnitsSold += unitsSold;
        productSalesData.push({ product, unitsSold });
      }

      // Calculate market share for each product
      const marketShares: MarketShareData[] = [];
      const competitorMarketShares = new Map<string, number>();
      let playerMarketShare = 0;

      for (const { product, unitsSold } of productSalesData) {
        const marketShare = totalUnitsSold > 0 ? (unitsSold / totalUnitsSold) : 0;
        
        marketShares.push({
          productId: product.id,
          companyId: product.metadata.companyId,
          productName: product.name,
          unitsSold,
          marketShare,
          previousMarketShare: product.salesMetrics?.marketShare
        });

        // Aggregate by company
        if (product.metadata.companyId === 'player') {
          playerMarketShare += marketShare;
        } else {
          const currentShare = competitorMarketShares.get(product.metadata.companyId) || 0;
          competitorMarketShares.set(product.metadata.companyId, currentShare + marketShare);
        }

        // Update the product's market share in the database
        await ProductManager.updateSalesMetrics(product.id, {
          marketShare: marketShare
        });
      }

      this.lastUpdateTime = currentGameDay;

      const analysis: MarketAnalysis = {
        totalMarketSize: this.estimateMarketSize(totalUnitsSold),
        totalUnitsSold,
        marketShares,
        playerMarketShare,
        competitorMarketShares,
        lastUpdated: new Date()
      };

      // Cache the analysis
      this.cacheMarketAnalysis(analysis);

      // Log significant market share changes
      this.logMarketShareChanges(marketShares);

      return analysis;
    } catch (error) {
      console.error('Error calculating market shares:', error);
      throw error;
    }
  }

  /**
   * Get company-level market share (aggregated across all their products)
   */
  public async getCompanyMarketShare(companyId: string): Promise<number> {
    const analysis = await this.calculateMarketShares(0); // Use cached data
    
    if (companyId === 'player') {
      return analysis.playerMarketShare;
    }
    
    return analysis.competitorMarketShares.get(companyId) || 0;
  }

  /**
   * Get top products by market share
   */
  public async getTopProducts(limit: number = 10): Promise<MarketShareData[]> {
    const analysis = await this.calculateMarketShares(0); // Use cached data
    return analysis.marketShares
      .sort((a, b) => b.marketShare - a.marketShare)
      .slice(0, limit);
  }

  /**
   * Estimate total market size based on current sales
   */
  private estimateMarketSize(totalUnitsSold: number): number {
    // Assume current sales represent a portion of the total addressable market
    // This can be adjusted based on game balance
    const marketPenetration = 0.2; // Assume 20% market penetration
    return Math.floor(totalUnitsSold / marketPenetration);
  }

  /**
   * Log significant market share changes for event system
   */
  private logMarketShareChanges(marketShares: MarketShareData[]): void {
    for (const data of marketShares) {
      if (data.previousMarketShare !== undefined) {
        const change = data.marketShare - data.previousMarketShare;
        const percentChange = Math.abs(change / (data.previousMarketShare || 0.01)) * 100;
        
        if (percentChange > 10) { // Significant change threshold
          // console.log(
          //   `📈 Market Share Change: ${data.productName} ${change > 0 ? '↑' : '↓'} ` +
          //   `${(change * 100).toFixed(2)}% (now ${(data.marketShare * 100).toFixed(2)}%)`
          // );
        }
      }
    }
  }

  /**
   * Cache market analysis for performance
   */
  private cachedAnalysis: MarketAnalysis | null = null;

  private cacheMarketAnalysis(analysis: MarketAnalysis): void {
    this.cachedAnalysis = analysis;
  }

  private getCachedMarketAnalysis(): MarketAnalysis {
    if (!this.cachedAnalysis) {
      return {
        totalMarketSize: 0,
        totalUnitsSold: 0,
        marketShares: [],
        playerMarketShare: 0,
        competitorMarketShares: new Map(),
        lastUpdated: new Date()
      };
    }
    return this.cachedAnalysis;
  }

  /**
   * Force a market share recalculation
   */
  public async forceUpdate(currentGameDay: number): Promise<MarketAnalysis> {
    this.lastUpdateTime = 0; // Reset to force update
    return this.calculateMarketShares(currentGameDay);
  }

  /**
   * Reset the service cache (primarily for testing)
   */
  public resetCache(): void {
    this.lastUpdateTime = 0;
    this.cachedAnalysis = null;
  }

  /**
   * Reset the service to initial state
   */
  public reset(): void {
    // console.log('📊 Market Share Service: Resetting to initial state');
    
    // Reset timing
    this.lastUpdateTime = 0;
    this.updateInterval = 30; // Reset to default 30 days
    
    // Clear cached analysis
    this.cachedAnalysis = null;
    
    // console.log('📊 Market Share Service: Reset complete');
  }
}

// Export singleton instance
export const marketShareService = MarketShareService.getInstance();